'use client'

import { useTranslations } from 'next-intl'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { popModal } from '@/components/modals'

const contactFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  email: z.string().email('Invalid email address'),
  content: z
    .string()
    .min(1, 'Message is required')
    .max(5000, 'Message is too long'),
})

type TContactFormData = z.infer<typeof contactFormSchema>

export default function ContactUsModal() {
  const t = useTranslations('modals.contact-us')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
  })

  /**
   * Handles form submission by sending contact data to API endpoint
   */
  const onSubmit = async (data: TContactFormData) => {
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        toast.success(t('form.success'))
        reset()
        popModal()
      } else {
        toast.error(result.message || t('form.error'))
      }
    } catch (error) {
      console.error('Contact form error:', error)
      toast.error(t('form.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogContent className="sm:max-w-[600px] px-10 py-6">
      <DialogHeader>
        <DialogTitle className="text-xl font-bold">{t('title')}</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium">
            {t('form.name_label')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Input
            id="name"
            type="text"
            placeholder={t('form.name_placeholder')}
            {...register('name')}
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            {t('form.email_label')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Input
            id="email"
            type="email"
            placeholder={t('form.email_placeholder')}
            {...register('email')}
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="content" className="text-sm font-medium">
            {t('form.content_label')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Textarea
            id="content"
            placeholder={t('form.content_placeholder')}
            {...register('content')}
            disabled={isSubmitting}
            rows={6}
            className="resize-none"
          />
          {errors.content && (
            <p className="text-sm text-red-500">{errors.content.message}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          variant="active"
        >
          {isSubmitting ? t('form.submitting') : t('form.submit')}
        </Button>
      </form>
    </DialogContent>
  )
}
